import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { startWith, map, takeUntil } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';

import { ClientIntegrationService } from '../../services/client-integration.service';
import { ToastService } from '../../services/toast.service';
import { ClientInformation } from '../../models/client-information';
import { Integration } from '../../models/integration';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { CustomSQLQueryService } from '../../services/sql-queries.service';
import { CustomSQLQuery } from '../../models/sql-query';
import { QueryType } from '../../models/query-type';

@Component({
  selector: 'app-add-sql-query',
  templateUrl: './add-sql-query.component.html',
  styleUrls: ['./add-sql-query.component.scss'],
  providers: [ClientIntegrationService, CustomSQLQueryService],
  standalone: false,
})
export class AddSQLQueryComponent implements OnInit, OnDestroy {

  componentDestroyed$ = new Subject<boolean>();
  @Input() isInTab = false;
  @Output() finished = new EventEmitter<string>();
  @Output() itemAdded = new EventEmitter<CustomSQLQuery>();
  @Output() cancel = new EventEmitter<boolean>();
  @Input() populatedAdd = false;

  @Input() client: ClientInformation = null;
  clients: ClientInformation[];
  filteredClients: Observable<ClientInformation[]>;
  clientFormControl = new UntypedFormControl();

  @Input() integration: Integration = null;
  integrations: Integration[];
  filteredIntegrations: Observable<Integration[]>;
  integrationFormControl = new UntypedFormControl();

  hasQueryType = false;
  queryType: QueryType = null;
  queryTypes: QueryType[] = [];
  filteredQueryTypes: Observable<QueryType[]>;
  queryTypeFormControl = new UntypedFormControl();

  newQuery: CustomSQLQuery = null;
  monacoData = '';
  queryName = '';

  private sub: any;
  dateNow: Date;
  checked = true;

  savingQuery = false;

  pageLoading = true;

  constructor(
    private clientService: ClientIntegrationService,
    private typeConstantService: TypeConstantService,
    private toastService: ToastService,
    private router: Router,
    private customQueryService: CustomSQLQueryService,
  ) {
  }

  ngOnInit(): void {

    this.getAllQueryTypes();
    if (this.client == null) {
      this.sub = this.clientService.getAllClients()
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (data) => {
            this.clients = data;
            this.filteredClients = this.clientFormControl.valueChanges.pipe(
              startWith(''),
              map(value => typeof value === 'string' ? value : value.name),
              map(name => name ? this._filterClients(name) : this.clients.slice()),
            );
          },
          error: (_error) => {
            this.toastService.toastCreate('Failed to load Clients', 'Warning');
          },
          complete: () => {
            this.pageLoading = false;
          },
        });
    }
    else if (this.integration == null) {
      this.getIntegrations();
    }
    else {
      this.pageLoading = false;
    }
    const date: Date = new Date();
    this.dateNow = date;
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  goBack(): void {
    this.cancel.emit(true);
  }

  toggleCancelEditIdentifier(): void {
    this.goBack();
  }

  selectClient(c: ClientInformation): void {
    this.pageLoading = true;
    this.client = c;
    this.getIntegrations();
  }

  editClient() {
    this.client = null;
    this.integration = null;
    this.clientFormControl.setValue('');
  }

  getClients(): void {
    this.clientService.getAllClients().subscribe((data) => {
      this.clients = data;
      this.filteredClients = this.clientFormControl.valueChanges.pipe(
        startWith(''),
        map(value => typeof value === 'string' ? value : value.name),
        map(name => name ? this._filterClients(name) : this.clients.slice()),
      );
    });
  }

  getAllQueryTypes(): void {
    this.customQueryService.getAllQueryTypes()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (data) => {
          this.queryTypes = data;
          this.filteredQueryTypes = this.queryTypeFormControl.valueChanges.pipe(
            startWith(''),
            map(value => typeof value === 'string' ? value : value.name),
            map(name => name ? this._filterQueryTypes(name) : this.queryTypes.slice()),
          );
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to load Query Types', 'Warning');
        },
        complete: () => {
          this.pageLoading = false;
        },
      });
  }

  editQueryType() {
    this.queryType = null;
    this.hasQueryType = false;
    this.queryTypeFormControl.setValue('');
    this.newQuery = null;
  }

  selectQueryType(q: QueryType): void {
    this.queryType = q;
    this.hasQueryType = true;

    this.newQuery = new CustomSQLQuery();
    this.newQuery.customSQLQueryId = '00000000-0000-0000-0000-000000000000';
    this.newQuery.integrationId = this.integration.integrationId;
    this.newQuery.sqlString = '';
    this.newQuery.queryTypeId = this.queryType.queryTypeId;
  }

  selectIntegration(i: Integration): void {
    this.integration = i;
  }

  editIntegration() {
    this.integration = null;
    this.integrationFormControl.setValue('');
  }

  getIntegrations(): void {
    if (this.client) {
      this.clientService.getIntegrationsByClient(this.client.clientDisplayName)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (data) => {
            this.integrations = data;
            this.filteredIntegrations = this.integrationFormControl.valueChanges.pipe(
              startWith(''),
              map(value => typeof value === 'string' ? value : value.name),
              map(name => name ? this._filterIntegrations(name) : this.integrations.slice()),
            );
          },
          error: (_error) => {
            this.toastService.toastCreate('Failed to load Client Integrations', 'Warning');
          },
          complete: () => {
            this.pageLoading = false;
          }
        });
    }
    else {
      this.toastService.toastCreate('No Client Selected.', 'Warning');
    }
  }

  addCustomQuery(): void {
    this.savingQuery = true;
    this.newQuery.sqlString = this.monacoData;
    this.newQuery.queryName = this.queryName;

    this.customQueryService.addCustomQuery(this.newQuery)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (value) => {
          this.toastService.toastCreate('Custom Query ' + this.newQuery.queryName + 'Added.', 'Success');
          this.newQuery.customSQLQueryId = value;
          this.itemAdded.emit(this.newQuery);
          this.savingQuery = false;
        },
        error: (_error) => {
          this.finished.emit('error');
          this.savingQuery = false;
        },
      });
  }

  /* Angular Material Functions */

  private _filterClients(value: string): ClientInformation[] {
    const filterValue = value.toLowerCase();
    return this.clients.filter(option => option.clientDisplayName.toLowerCase().includes(filterValue));
  }

  private _filterIntegrations(value: string): Integration[] {
    const filterValue = value.toLowerCase();
    return this.integrations.filter(option => option.name.toLowerCase().includes(filterValue));
  }

  private _filterQueryTypes(value: string): QueryType[] {
    const filterValue = value.toLowerCase();
    return this.queryTypes.filter(option => option.queryTypeName.toLowerCase().includes(filterValue));
  }

  displayFnClient(x: ClientInformation): string {
    return x && x.clientDisplayName ? x.clientDisplayName : '';
  }

  displayFnIntegration(x: Integration): string {
    return x && x.name ? x.name : '';
  }

  displayFnQueryType(x: QueryType): string {
    return x && x.queryTypeName ? x.queryTypeName : '';
  }
}
